import {
  createContext,
  useContext,
  useReducer,
  ReactNode
} from "react";
import { useEnvContext } from "../../context/EnvironmentContext";

import {
  useCWnrcPurchase,
  useCWsegmentEvent,
  useCWsubscribe,
  useCWuserAgeEvent,
  useCWuserGenderEvent,
} from "../hooks/campaignswellAnalytics";

export interface EventMetadata {
  description: string;
  category: string;
  expectedData: string[];
  source: string;
}

export interface EventType {
  key: string;
  label: string;
  metadata: EventMetadata;
}

interface PurchaseEventData {
  transaction_id: string;
  customer_id: string;
  client_user_id: string | null;
  product_id: string;
  product_price: number;
  currency?: string;
}

interface SegmentEventData {
  segment: string;
  ab_test: string;
}

interface SubscribeEventData {
  subscription_id: string;
  customer_id: string;
  transaction_id: string;
  client_user_id: string | null;
  product_price: number;
  product_id: string;
  currency?: string;
}

interface UserAgeEventData {
  age: string;
}

interface UserGenderEventData {
  gender: string;
}

export type EventData =
  | PurchaseEventData
  | SegmentEventData
  | SubscribeEventData
  | UserAgeEventData
  | UserGenderEventData;

export interface BaseResult {
  eventType: string;
  category: string;
  source: string;
  timestamp: string;
  processedData?: Record<string, unknown>;
}

export type TriggerEventFn = (
  eventType: EventType,
  eventData: EventData
) => Promise<BaseResult>;

interface EventState {
  status: "pending" | "success" | "error" | null;
  result: BaseResult | Error | null;
}

interface CampaignswellState {
  events: Record<string, EventState>;
}

type Action =
  | { type: "TRIGGER_EVENT_START"; eventType: EventType }
  | { type: "TRIGGER_EVENT_SUCCESS"; eventType: EventType; result: BaseResult }
  | { type: "TRIGGER_EVENT_ERROR"; eventType: EventType; error: Error };

interface CampaignswellContextType {
  state: CampaignswellState;
  triggerEvent: (eventType: EventType, eventData: EventData) => Promise<BaseResult>;
  EVENT_TYPES: Record<string, EventType>;
}

const CampaignswellContext = createContext<CampaignswellContextType | undefined>(
  undefined
);

export const useCampaignswell = (): CampaignswellContextType => {
  const context = useContext(CampaignswellContext);
  if (!context) {
    throw new Error("useCampaignswell must be used within CampaignswellProvider");
  }
  return context;
};

const EVENT_TYPES: Record<string, EventType> = {
  CW_PURCHASE: {
    key: "cw_purchase",
    label: "CW Purchase Event",
    metadata: {
      description: "Track user purchase events",
      category: "revenue",
      expectedData: [
        "transaction_id",
        "customer_id",
        "client_user_id",
        "product_id",
        "product_price",
      ],
      source: "product_checkout",
    },
  },
  CW_SEGMENT: {
    key: "cw_segment",
    label: "CW Segment Event",
    metadata: {
      description: "Track user A-B test segment assignment",
      category: "segmentation",
      expectedData: ["segment", "ab_test"],
      source: "ab_testing",
    },
  },
  CW_SUBSCRIBE: {
    key: "cw_subscribe",
    label: "CW Subscribe Event",
    metadata: {
      description: "Track user subscription events",
      category: "revenue",
      expectedData: [
        "subscription_id",
        "customer_id",
        "transaction_id",
        "client_user_id",
        "product_price",
        "product_id",
      ],
      source: "subscription_checkout",
    },
  },
  CW_USER_AGE: {
    key: "cw_user_age",
    label: "CW User Age Event",
    metadata: {
      description: "Track user age information",
      category: "user_profile",
      expectedData: ["age_range"],
      source: "user_onboarding",
    },
  },
  CW_USER_GENDER: {
    key: "cw_user_gender",
    label: "CW User Gender Event",
    metadata: {
      description: "Track user gender information",
      category: "user_profile",
      expectedData: ["gender"],
      source: "user_onboarding",
    },
  },
};

const EventLogger = {
  start: (eventType: EventType, data: EventData): void => {
    console.group(`🚀 ${eventType.label} Started`);
    console.log({
      event: {
        type: eventType.key,
        category: eventType.metadata.category,
        description: eventType.metadata.description,
        source: eventType.metadata.source,
      },
      timestamp: new Date().toISOString(),
      inputData: data,
      expectedFields: eventType.metadata.expectedData,
    });
    console.groupEnd();
  },

  success: (eventType: EventType, result: BaseResult): void => {
    console.group(`✅ ${eventType.label} Completed`);
    console.log({
      event: {
        type: eventType.key,
        category: eventType.metadata.category,
        source: eventType.metadata.source,
      },
      timestamp: new Date().toISOString(),
      result: result,
      validation: {
        expectedFields: eventType.metadata.expectedData,
        receivedFields: Object.keys(result.processedData || {}),
        isComplete: eventType.metadata.expectedData.every(
          (field) =>
            result.processedData &&
            Object.keys(result.processedData).includes(field)
        ),
      },
    });
    console.groupEnd();
  },

  error: (eventType: EventType, error: Error): void => {
    console.group(`❌ ${eventType.label} Failed`);
    console.error({
      event: {
        type: eventType.key,
        category: eventType.metadata.category,
        source: eventType.metadata.source,
      },
      timestamp: new Date().toISOString(),
      error: {
        type: error.name,
        message: error.message,
        isValidationError: error.message.includes("Validation failed"),
        details: error.message.includes("Validation failed")
          ? error.message.split("Validation failed: ")[1].split(", ")
          : null,
        stack: error.stack,
      },
    });
    console.groupEnd();
  },
};

class EventValidator {
  static validateField(value: unknown, fieldName: string): boolean {
    if (value === undefined || value === null || value === "") {
      throw new Error(`Field "${fieldName}" is required but got: ${value}`);
    }
    return true;
  }

  static validateEventData(data: EventData, expectedFields: string[]): boolean {
    const errors: string[] = [];

    expectedFields.forEach((field) => {
      try {
        this.validateField((data as any)[field], field);
      } catch (error) {
        errors.push((error as Error).message);
      }
    });

    if (errors.length > 0) {
      throw new Error(`Validation failed: ${errors.join(", ")}`);
    }

    return true;
  }
}

interface EventFactoryResult {
  hook: (data: any) => void;
  data: any;
  formatResult: () => BaseResult;
}

class EventFactory {
  static createEvent(type: EventType, data: EventData): EventFactoryResult {
    const baseResult: Omit<BaseResult, "processedData"> = {
      eventType: type.key,
      category: type.metadata.category,
      source: type.metadata.source,
      timestamp: new Date().toISOString(),
    };

    switch (type.key) {
      case EVENT_TYPES.CW_PURCHASE.key: {
        EventValidator.validateEventData(data, [
          "transaction_id",
          "customer_id",
          "client_user_id",
          "product_id",
          "product_price",
        ]);

        const purchaseData = data as PurchaseEventData;

        return {
          hook: useCWnrcPurchase,
          data: {
            cwPurchase: {
              transaction_id: purchaseData.transaction_id,
              customer_id: purchaseData.customer_id,
              client_user_id: purchaseData.client_user_id,
              product_id: purchaseData.product_id,
              product_price: purchaseData.product_price,
              currency: purchaseData.currency,
            },
          },
          formatResult: () => ({
            ...baseResult,
            processedData: {
              transaction_id: purchaseData.transaction_id,
              customer_id: purchaseData.customer_id,
              client_user_id: purchaseData.client_user_id,
              product_id: purchaseData.product_id,
              product_price: purchaseData.product_price,
              currency: purchaseData.currency,
            },
          }),
        };
      }

      case EVENT_TYPES.CW_SEGMENT.key: {
        EventValidator.validateEventData(data, ["segment", "ab_test"]);

        const segmentData = data as SegmentEventData;

        return {
          hook: useCWsegmentEvent,
          data: {
            cwSegment: {
              ab_test_name: segmentData.segment,
              ab_test_variant: segmentData.ab_test,
            },
          },
          formatResult: () => ({
            ...baseResult,
            processedData: {
              segment: segmentData.segment,
              ab_test: segmentData.ab_test,
            },
          }),
        };
      }

      case EVENT_TYPES.CW_SUBSCRIBE.key: {
        EventValidator.validateEventData(data, [
          "subscription_id",
          "customer_id",
          "transaction_id",
          "client_user_id",
          "product_price",
          "product_id",
        ]);

        const subscribeData = data as SubscribeEventData;

        return {
          hook: useCWsubscribe,
          data: {
            cwSubscribe: {
              subscription_id: subscribeData.subscription_id,
              customer_id: subscribeData.customer_id,
              transaction_id: subscribeData.transaction_id,
              client_user_id: subscribeData.client_user_id,
              product_price: subscribeData.product_price,
              product_id: subscribeData.product_id,
              currency: subscribeData.currency,
            },
          },
          formatResult: () => ({
            ...baseResult,
            processedData: {
              subscription_id: subscribeData.subscription_id,
              customer_id: subscribeData.customer_id,
              transaction_id: subscribeData.transaction_id,
              client_user_id: subscribeData.client_user_id,
              price: subscribeData.product_price,
              product_id: subscribeData.product_id,
              currency: subscribeData.currency,
            },
          }),
        };
      }

      case EVENT_TYPES.CW_USER_AGE.key: {
        EventValidator.validateEventData(data, ["age"]);

        const ageData = data as UserAgeEventData;

        return {
          hook: useCWuserAgeEvent,
          data: {
            csUserAge: {
              age_range: ageData.age,
            },
          },
          formatResult: () => ({
            ...baseResult,
            processedData: {
              age_range: ageData.age,
            },
          }),
        };
      }

      case EVENT_TYPES.CW_USER_GENDER.key: {
        EventValidator.validateEventData(data, ["gender"]);

        const genderData = data as UserGenderEventData;

        return {
          hook: useCWuserGenderEvent,
          data: {
            userGender: {
              gender: genderData.gender,
            },
          },
          formatResult: () => ({
            ...baseResult,
            processedData: {
              gender: genderData.gender,
            },
          }),
        };
      }

      default:
        throw new Error(`Unknown event type: ${type.key}`);
    }
  }
}

interface CampaignswellProviderProps {
  children: ReactNode;
}

export const CampaignswellProvider = ({ children }: CampaignswellProviderProps) => {
  const { isProduction } = useEnvContext();

  const initialState: CampaignswellState = {
    events: Object.values(EVENT_TYPES).reduce(
      (acc, type) => ({
        ...acc,
        [type.key]: {
          status: null,
          result: null,
        },
      }),
      {}
    ),
  };

  const reducer = (state: CampaignswellState, action: Action): CampaignswellState => {
    switch (action.type) {
      case "TRIGGER_EVENT_START":
        return {
          ...state,
          events: {
            ...state.events,
            [action.eventType.key]: {
              status: "pending",
              result: null,
            },
          },
        };

      case "TRIGGER_EVENT_SUCCESS":
        return {
          ...state,
          events: {
            ...state.events,
            [action.eventType.key]: {
              status: "success",
              result: action.result,
            },
          },
        };

      case "TRIGGER_EVENT_ERROR":
        return {
          ...state,
          events: {
            ...state.events,
            [action.eventType.key]: {
              status: "error",
              result: action.error,
            },
          },
        };

      default:
        return state;
    }
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const triggerEvent = async (eventType: EventType, eventData: EventData): Promise<BaseResult> => {
    if (!eventType || !eventType.key || !eventType.label) {
      console.error("Invalid event type:", eventType);
      throw new Error("Invalid event type provided");
    }

    if (!eventData) {
      console.error("No event data provided");
      throw new Error("Event data is required");
    }

    try {
      !isProduction && EventLogger.start(eventType, eventData);

      dispatch({ type: "TRIGGER_EVENT_START", eventType });

      const event = EventFactory.createEvent(eventType, eventData);
      event.hook(event.data);

      const formattedResult = event.formatResult();

      !isProduction && EventLogger.success(eventType, formattedResult);
      dispatch({
        type: "TRIGGER_EVENT_SUCCESS",
        eventType,
        result: formattedResult,
      });

      return formattedResult;
    } catch (error) {
      EventLogger.error(eventType, error as Error);
      dispatch({
        type: "TRIGGER_EVENT_ERROR",
        eventType,
        error: error as Error,
      });
      throw error;
    }
  };

  const contextValue: CampaignswellContextType = {
    state,
    triggerEvent,
    EVENT_TYPES,
  };

  return (
    <CampaignswellContext.Provider value={contextValue}>
      {children}
    </CampaignswellContext.Provider>
  );
};
